import React from 'react';
import {
  VStack, HStack, Text, Icon, Button, ButtonText, Spinner, Heading, Center, Badge, BadgeText,
} from '@gluestack-ui/themed';
import { AlertCircle, Calendar, Layers } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import {
  formatEnglishDateWithYear,
  formatEnglishTime,
  formatEnglishTimezoneAbbr,
} from '@/common/utils/formatEnglishDateTime';
import FormField from '../../../../../../common/components/forms/FormField';

const EventSlotSelection = ({
  slots,
  loading,
  error,
  supportEmail,
  onBook,
  bookingPending,
  isBookingAgain,
  onCancelBookingAgain,
  moduleId,
  insideSection = false,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const selectedSlotId = watch('slotId');

  if (loading) {
    return (
      <Center py="$8" flexDirection="column" space="sm">
        <Spinner />
        <Text size="sm">{t('student.eventTask.checkingSlots')}</Text>
      </Center>
    );
  }

  return (
    <VStack space="md">
      <HStack justifyContent="space-between" alignItems="center">
        {insideSection ? (
          <Text size="sm" color="$textSubtle">{t('student.eventTask.chooseSession')}</Text>
        ) : (
          <Heading size="sm">{t('student.eventTask.chooseSession')}</Heading>
        )}
        {isBookingAgain && (
          <Button variant="link" size="sm" onPress={onCancelBookingAgain}>
            <ButtonText>{t('common.buttons.cancel')}</ButtonText>
          </Button>
        )}
      </HStack>

      {error && (
        <HStack alignItems="center" space="sm" p="$3" bg="$error50" borderRadius="$md">
          <Icon as={AlertCircle} size="sm" color="$error500" />
          <Text color="$error500" fontSize={13}>
            {t('student.eventTask.loadError')}
          </Text>
        </HStack>
      )}

      {slots.length === 0 && !error && (
        <VStack space="sm" p="$4" bg="$backgroundLight50" borderRadius="$md" alignItems="center">
          <Icon as={Calendar} size="lg" color="$textSubtle" />
          <Text color="$textSubtle" fontSize={13} textAlign="center">
            {t('student.eventTask.noSlotsSupport', { email: supportEmail })}
          </Text>
        </VStack>
      )}

      {slots.length > 0 && !error && (
        <VStack space="sm">
          <FormField
            name="slotId"
            type="radio"
            label=""
            fieldSize="sm"
            choices={slots.slice(0, 4).map((slot) => {
              const dateObj = new Date(slot.startTime);
              const dateStr = formatEnglishDateWithYear(dateObj);
              const formattedStartTime = formatEnglishTime(dateObj);
              const formattedEndTime = formatEnglishTime(slot.endTime);
              const spots = slot.capacity > 1 ? ` (${t('student.eventTask.placesLeft', { count: slot.availableSpots })})` : '';
              const tzAbbr = formatEnglishTimezoneAbbr(dateObj);
              const label = t('student.eventTask.slotOption', {
                date: dateStr,
                time: `${formattedStartTime} - ${formattedEndTime}`,
                spots,
                tz: tzAbbr,
              });

              return { value: slot.slotId, label };
            })}
          />

          {/* Timezone badge */}
          {(() => {
            const dateNow = new Date();
            const tzAbbr = formatEnglishTimezoneAbbr(dateNow);
            return (
              <Badge size="sm" variant="solid" alignSelf="flex-start">
                <BadgeText>{tzAbbr}</BadgeText>
              </Badge>
            );
          })()}

          {/* Selected slot bound modules/tasks details */}
          {selectedSlotId
            && (() => {
              const selected = slots.find((s) => s.slotId === selectedSlotId);
              const bound = selected?.boundTasks || [];
              if (!bound.length) return null;
              // Only show when the slot covers any module other than the current one
              const distinctModuleIds = Array.from(new Set(bound.map((b) => b.moduleId)));
              const hasOtherModules = distinctModuleIds.some((id) => id !== moduleId);
              if (!hasOtherModules) return null;
              return (
                <VStack space="xs" bg="$backgroundLight50" borderRadius="$md" p="$3">
                  <HStack alignItems="center" space="sm">
                    <Icon as={Layers} size="xs" color="$textSubtle" />
                    <Text size="xs" color="$textSubtle" fontWeight="semibold">
                      {t('student.eventTask.sessionModulesTitle', 'Selected session covers')}
                    </Text>
                  </HStack>
                  <VStack space="xs">
                    {bound.map((bt, idx) => (
                      <HStack key={`${bt.moduleId}_${bt.taskId || idx}`} alignItems="center" space="xs" pl="$5">
                        <Text size="xs" color="$textBody">
                          {(bt.moduleTitle || t('student.eventTask.unknownModule', 'Module'))
                            + (bt.taskTitle ? `: ${bt.taskTitle}` : '')}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              );
            })()}

          <Button
            size="sm"
            onPress={onBook}
            variant="solid"
            action="primary"
            isDisabled={!selectedSlotId || bookingPending}
          >
            <ButtonText>{t('student.eventTask.registrationButton')}</ButtonText>
          </Button>
        </VStack>
      )}
    </VStack>
  );
};

export default EventSlotSelection;
